import React, { useState, useEffect } from "react";
import { Avatar, AvatarProps } from "@mui/material";
import {
  testImageAccess,
  convertFirebaseStorageUrl,
  createFallbackAvatarUrl,
  logImageAccessDiagnostics,
} from "../utils/imageAccessHelper";

interface SafeAvatarProps extends Omit<AvatarProps, "src"> {
  src?: string;
  alt: string;
  fallbackText: string;
}

/**
 * SafeAvatar component that handles image loading errors gracefully
 * and provides fallback for broken Firebase Storage URLs
 */
const SafeAvatar: React.FC<SafeAvatarProps> = ({
  src,
  alt,
  fallbackText,
  children,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(!!src);
  const [retryCount, setRetryCount] = useState(0);
  const [useFallbackAvatar, setUseFallbackAvatar] = useState(false);

  useEffect(() => {
    if (src) {
      // Convert Firebase Storage URL if needed
      const convertedUrl = convertFirebaseStorageUrl(src);
      setImageSrc(convertedUrl);
      setHasError(false);
      setIsLoading(true);

      // Test image access and log diagnostics in development
      if (process.env.NODE_ENV === "development") {
        logImageAccessDiagnostics(
          convertedUrl,
          `SafeAvatar for ${fallbackText}`
        );
      }
    } else {
      setImageSrc(undefined);
      setHasError(false);
      setIsLoading(false);
    }
  }, [src, fallbackText]);

  const handleImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const imgElement = event.currentTarget;
    const currentRetry = retryCount;

    console.warn(
      `[SafeAvatar] Failed to load profile picture (attempt ${
        currentRetry + 1
      }): ${src}`,
      {
        error: event.type,
        naturalWidth: imgElement.naturalWidth,
        naturalHeight: imgElement.naturalHeight,
        complete: imgElement.complete,
        currentURL: window.location.href,
        hasAuthToken: !!localStorage.getItem("authToken"),
        domain: window.location.hostname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        retryAttempt: currentRetry + 1,
      }
    );

    // Try to retry loading the image (max 2 retries)
    if (currentRetry < 2 && src) {
      setTimeout(() => {
        console.info(
          `[SafeAvatar] Retrying image load (attempt ${currentRetry + 2})`
        );
        setRetryCount(currentRetry + 1);
        setImageSrc(
          convertFirebaseStorageUrl(src) +
            `?retry=${currentRetry + 1}&t=${Date.now()}`
        );
      }, 1000 * (currentRetry + 1)); // Progressive delay: 1s, 2s, 3s
      return;
    }

    // Log additional network/CORS information for debugging
    if (src) {
      fetch(src, { method: "HEAD", mode: "no-cors" })
        .then(() => {
          console.info(
            `[SafeAvatar] Image URL is reachable but may have CORS issues: ${src}`
          );
        })
        .catch((fetchError) => {
          console.error(
            `[SafeAvatar] Image URL is not reachable: ${src}`,
            fetchError
          );
        });
    }

    // After all retries failed, try to use a generated avatar
    const fallbackUrl = createFallbackAvatarUrl(fallbackText);
    setUseFallbackAvatar(true);
    setImageSrc(fallbackUrl);
    setIsLoading(false);
  };

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    console.log(`[SafeAvatar] Successfully loaded profile picture: ${src}`, {
      naturalWidth: event.currentTarget.naturalWidth,
      naturalHeight: event.currentTarget.naturalHeight,
      currentURL: window.location.href,
      hasAuthToken: !!localStorage.getItem("authToken"),
    });
    setIsLoading(false);
  };

  // If there's no src, show text fallback. If we're using fallback avatar, show that.
  const shouldShowTextFallback = !imageSrc && !useFallbackAvatar;
  const shouldShowImage = imageSrc && !shouldShowTextFallback;

  return (
    <Avatar
      {...props}
      src={shouldShowImage ? imageSrc : undefined}
      alt={alt}
      onError={handleImageError}
      onLoad={handleImageLoad}
      sx={{
        ...props.sx,
        // Add a subtle border for fallback avatars to make them look more polished
        ...(useFallbackAvatar && {
          border: "1px solid rgba(0, 0, 0, 0.1)",
        }),
      }}
    >
      {shouldShowTextFallback ? fallbackText.charAt(0).toUpperCase() : children}
    </Avatar>
  );
};

export default SafeAvatar;
